import { useMemo } from "react"
import { Table, Tag } from "antd"
import type { ColumnsType } from "antd/es/table"
import type { Key } from "react"
import dayjs from "dayjs"
import { TableActionColumn } from "@/components/TableActionColumn"
import { ID } from "@/types/common"
import { Lead, LeadTableProps } from "../types"

const LeadTable = ({
  leads,
  onEdit,
  onDelete,
  loading = false,
  selectedIds = [],
  onSelectionChange,
  sourceOptions,
  retentionTypeOptions,
  pagination,
  onPageChange,
}: LeadTableProps) => {
  const sourceLabelMap = useMemo(
    () => new Map(sourceOptions.map((option) => [option.value, option.label])),
    [sourceOptions]
  )
  const retentionTypeLabelMap = useMemo(
    () =>
      new Map(retentionTypeOptions.map((option) => [option.value, option.label])),
    [retentionTypeOptions]
  )

  const columns: ColumnsType<Lead> = useMemo(
    () => [
      {
        title: "学生姓名",
        dataIndex: "studentName",
        key: "studentName",
        width: 120,
        render: (value?: string) => value || "-",
        sorter: (a: Lead, b: Lead) =>
          (a.studentName || "").localeCompare(b.studentName || "", "zh"),
      },
      {
        title: "日期",
        dataIndex: "leadAt",
        key: "leadAt",
        width: 190,
        render: (value: string) => dayjs(value).format("YYYY-MM-DD HH:mm:ss"),
        sorter: (a: Lead, b: Lead) => dayjs(a.leadAt).unix() - dayjs(b.leadAt).unix(),
      },
      {
        title: "来源",
        dataIndex: "sourceType",
        key: "sourceType",
        width: 120,
        render: (value: string) => (
          <Tag color="blue">{sourceLabelMap.get(value) || value}</Tag>
        ),
      },
      {
        title: "留资形式",
        dataIndex: "retentionType",
        key: "retentionType",
        width: 120,
        render: (value?: string) =>
          value ? <Tag color="cyan">{retentionTypeLabelMap.get(value) || value}</Tag> : "-",
      },
      {
        title: "是否加微信",
        dataIndex: "wechatAdded",
        key: "wechatAdded",
        width: 104,
        render: (value?: boolean) => (
          <Tag color={value ? "green" : "default"}>{value ? "是" : "否"}</Tag>
        ),
      },
      {
        title: "是否到店评估",
        dataIndex: "storeEvaluated",
        key: "storeEvaluated",
        width: 124,
        render: (value?: boolean) => (
          <Tag color={value ? "green" : "default"}>{value ? "是" : "否"}</Tag>
        ),
      },
      {
        title: "评估备注",
        dataIndex: "evaluationNote",
        key: "evaluationNote",
        width: 180,
        ellipsis: true,
        responsive: ["xl"],
        render: (value?: string) => value || "-",
      },
      {
        title: "体验课备注",
        dataIndex: "trialLessonNote",
        key: "trialLessonNote",
        width: 180,
        ellipsis: true,
        responsive: ["xl"],
        render: (value?: string) => value || "-",
      },
      {
        title: "备注",
        dataIndex: "note",
        key: "note",
        width: 180,
        ellipsis: true,
        responsive: ["xl"],
        render: (value?: string) => value || "-",
      },
      TableActionColumn<Lead>({
        onEdit,
        onDelete: (record: Lead | ID) => {
          const id = typeof record === "object" && record !== null ? record.id : record
          onDelete(id)
        },
        getId: (record) => record.id,
        deleteConfirmDescription: (record) =>
          `确定要删除客资 "${record.studentName || record.sourceType}" 吗？`,
        width: 160,
      }) as any,
    ],
    [onDelete, onEdit, retentionTypeLabelMap, sourceLabelMap]
  )

  const rowSelection = onSelectionChange
    ? {
        selectedRowKeys: selectedIds,
        onChange: (selectedRowKeys: Key[]) => {
          onSelectionChange(selectedRowKeys as ID[])
        },
        getCheckboxProps: (record: Lead) => ({
          name: record.studentName || record.sourceType,
        }),
      }
    : undefined

  return (
    <Table
      columns={columns}
      dataSource={leads}
      rowKey="id"
      loading={loading}
      rowSelection={rowSelection}
      tableLayout="fixed"
      scroll={{ x: 1320 }}
      pagination={{
        current: pagination?.page,
        pageSize: pagination?.pageSize,
        total: pagination?.total,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
        pageSizeOptions: ["10", "20", "50", "100"],
        onChange: (page, pageSize) => {
          onPageChange?.(page, pageSize)
        },
      }}
      size="middle"
      showSorterTooltip={false}
    />
  )
}

export default LeadTable
